import React, { useContext, useMemo, useState } from 'react';
import { Button, DatePicker, message, Table } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import AttributePicker from 'common/components/AttributePicker';
import { AttributesContext } from 'common/contexts';
import { simpleFetch } from 'common/utils/http';
import { borderGrey } from 'common/constants/colors';
import UIDLink from 'common/components/UIDLink';
import UIDEditModal from 'common/components/UIDEditModal';
import PaneHeader from './components/PaneHeader';

const sortByTimeValue = (a, b) => a.time_value < b.time_value ? 1 : -1;

const auditLogColumns = [
  {
    title: 'Action',
    dataIndex: 'action',
    filters: [
      { text: 'Addition', value: 'addition' },
      { text: 'Removal', value: 'removal' },
    ],
    onFilter: (value, record) => record.action === value,
  },
  {
    title: 'Person',
    dataIndex: 'person',
  },
  {
    title: 'Affected Person',
    dataIndex: 'affected_person',
  },
  {
    title: 'Attribute Type',
    dataIndex: 'attribute_type',
  },
  {
    title: 'Attribute Value',
    dataIndex: 'attribute_value',
  },
  {
    title: 'Time',
    dataIndex: 'time_value',
    sorter: sortByTimeValue,
  },
];

const AdminAuditLogPage = () => {
  const { attributes } = useContext(AttributesContext);

  const [attributeType, setAttributeType] = useState(null);
  const [personId, setPersonId] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [auditLogs, setAuditLogs] = useState(null);
  // showPersonModal will be a string because UIDEditModal takes attrName as a prop to determine whether modal is open
  const [attributeTypeName, setAttributeTypeName] = useState(null);

  const searchEnabled = attributeType || personId || startTime || endTime;

  const attributeName = useMemo(() => {
    if (!attributeType) {
      return null;
    }

    const [selectedGenre, selectedSection, selectedAttrType] = attributeType;
    try {
      return attributes.genres.find(genre => genre.name === selectedGenre)
        .sections.find(section => section.name === selectedSection)
        .attribute_types.find(attrType => attrType.name === selectedAttrType)
        .description;
    } catch {
      return selectedAttrType;
    }
  }, [attributeType, attributes]);

  const handleChangePerson = e => {
    e.preventDefault();
    setAttributeTypeName('person');
  };

  const handleSearchClick = async () => {
    if (searchEnabled) {
      const queries = [];
      if (attributeType) { queries.push(`attribute_type=${attributeType[2]}`) }
      if (personId) { queries.push(`person_id=${personId}`) }
      if (startTime) { queries.push(`start_time=${startTime}`) }
      if (endTime) { queries.push(`end_time=${endTime}`) }

      try {
        const response = await simpleFetch(`/api/v1/attributes/audit-history?${queries.join('&')}`);

        setAuditLogs(response.logs.sort(sortByTimeValue));
      } catch {
        message.error('Something went wrong. Please try again later');
        setAuditLogs([]);
      }
    }
  };

  /*
     in the new refactored component, updateValue is called on 2 args, but here we
     only want to set the person ID to the value.
     The attribute type doesn't matter here, so attr is a noop
    */
  const handleValuePerson = (attr, value) => {
    setPersonId(value);
  };
  return (
    <>
      <UIDEditModal
        singleton
        attrTypeName={attributeTypeName}
        attributes={[attributes]}
        onClose={() => setAttributeTypeName(null)}
        updateValue={handleValuePerson}
      />
      <div style={{ borderRight: `solid 1px ${borderGrey}`, width: '350px' }}>
        <PaneHeader paneName="Search" />
        <div style={{ margin: '20px' }}>
          {/* Select attribute type */}
          <div style={{ fontWeight: 'bold', margin: '20px 0' }}>Attribute Type</div>
          <AttributePicker
            excludeAttributes
            showUnlistable
            attributes={attributes}
            onChange={attrType => setAttributeType(attrType)}
          />
          {attributeType && (
            <div style={{ margin: '10px 0' }}>
              <Button onClick={() => setAttributeType(null)} type="primary">
                {attributeName}
                <CloseCircleOutlined />
              </Button>
            </div>
          )}

          {/* Select Person */}
          <div style={{ display: 'flex', margin: '20px 0' }}>
            <div style={{ flex: 1, fontWeight: 'bold' }}>Person</div>
            <div style={{ flex: 3 }}>
              {typeof personId === 'number' && <UIDLink uid={personId} />}
            </div>
            <div style={{ width: '100px' }}>
              <a href="/" onClick={handleChangePerson} style={{ float: 'right' } }>
                {`${personId ? 'Change' : 'Add'} person`}
              </a>
            </div>
          </div>

          {/* Select Start Time */}
          <div style={{ fontWeight: 'bold', margin: '20px 0' }}>Start Time</div>
          <DatePicker
            showTime
            onChange={time => setStartTime(time ? time.format() : null)}
            style={{ width: '100%' }}
          />

          {/* Select End Time */}
          <div style={{ fontWeight: 'bold', margin: '20px 0' }}>End Time</div>
          <DatePicker
            showTime
            onChange={time => setEndTime(time ? time.format() : null)}
            style={{ width: '100%' }}
          />

          <div style={{ margin: '20px 0' }}>
            <Button disabled={!searchEnabled} onClick={handleSearchClick} type="primary">Search</Button>
          </div>
        </div>
      </div>
      <div style={{ flex: '1' }}>
        <PaneHeader paneName="Audit Logs" />
        <Table columns={auditLogColumns} dataSource={auditLogs} />
      </div>
    </>
  );
};

export default AdminAuditLogPage;
